import {
  CheckCircleIcon,
  ChevronDownIcon,
  InfoIcon,
  PencilEdit02Icon,
  TrashIcon,
} from '@/icons';
import {
  Button,
  Collapse,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Tooltip,
  type FormInstance,
  type FormListFieldData,
} from 'antd';
import { useEffect, useState } from 'react';
import { useGetSalaryStructureQuery } from '@/app/features/employee/emp-payroll';
import ConfirmationModal from '@/components/ConfirmationModal';
import { EMPLOYEE_TYPES } from '@/constants/global.constants';
import {
  createNumberOptions,
  snakeToCamel,
} from '@/utils/string.utils';
const { Panel } = Collapse;

type Props = {
  form: FormInstance<any>;
  fields: FormListFieldData[];
  isEditMode: boolean;
  onRemove: (index: number | number[]) => void;
};

const LeaveTypes = ({ fields, form, isEditMode, onRemove }: Props) => {
  const policyTypes = Form.useWatch(['leave_types'], form) || [];

  return (
    <Collapse
      bordered={false}
      accordion={false}
      expandIconPosition="right"
      className="custom-collapse"
    >
      {fields?.map(({ name, key }) => {
        const policyStatus = policyTypes?.[name]?.policy_status;

        return (
          <>
            {policyStatus === 'completed' ? (
              <>
                <Panel
                  key={key}
                  forceRender={true}
                  style={{ borderRadius: 8 }}
                  header={
                    <TypeHeader
                      onRemove={() => onRemove(name)}
                      form={form}
                      name={name}
                    />
                  }
                  className="!p-3 !rounded-lg !border !border-theme-gray-700"
                >
                  <TypeBody isEditMode={isEditMode} form={form} name={name} />
                </Panel>
                <div className="h-3" />
              </>
            ) : (
              <TypeHeader key={key} form={form} name={name} />
            )}
          </>
        );
      })}
    </Collapse>
  );
};
export const TypeHeader = ({
  name,
  form,
  onRemove,
}: {
  name: number;
  form: FormInstance<any>;
  onRemove?: () => void;
}) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const status = Form.useWatch(['leave_types', name, 'policy_status'], form);
  const policyName = Form.useWatch(['leave_types', name, 'name'], form);

  const handleAction = async () => {
    if (status === 'created') {
      form.setFieldValue(['leave_types', name, 'policy_status'], 'editing');
    } else if (status === 'editing' && policyName) {
      form.setFieldValue(['leave_types', name, 'policy_status'], 'completed');
    } else {
      form.setFields([
        {
          name: ['leave_types', name, 'name'],
          errors: ['Please enter name'],
        },
      ]);
    }
  };
  return (
    <div>
      <div onClick={(e) => e.stopPropagation()}>
        <ConfirmationModal
          open={openConfirm}
          title="Delete Leave Type"
          subTitle="Are you sure you want to delete this leave type?"
          buttonName="Delete"
          handleClose={() => setOpenConfirm(false)}
          handleSubmit={() => {
            onRemove?.();
            setOpenConfirm(false);
          }}
        />
      </div>
      <Form.Item
        hidden
        noStyle
        initialValue={'created'}
        className="!mb-0  no-border"
        name={[name, 'policy_status']}
      >
        <Input />
      </Form.Item>
      {status === 'completed' ? (
        <div className="flex w-full justify-between">
          <div className="flex items-center gap-3">
            <h5 className="justify-start text-white text-xl font-medium">
              {policyName}
            </h5>
            <div onClick={(e) => e.stopPropagation()}>
              <Form.Item
                noStyle
                className="!mb-0"
                name={[name, 'status']}
                valuePropName="checked"
              >
                <Switch className="custom-switch" />
              </Form.Item>
            </div>
          </div>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setOpenConfirm(true);
            }}
            icon={<TrashIcon size={20} className="!text-theme-error-500" />}
            className="!size-8 !bg-transparent !border-theme-gray-700"
            classNames={{ icon: 'flex items-center justify-center size-8' }}
          />
        </div>
      ) : (
        <div className="p-4 h-[74px] flex items-center justify-between gap-2 rounded-lg border border-theme-slate-700 bg-theme-slate-800 mb-3 first:mt-3">
          {status === 'created' ? (
            <h5 className="justify-start text-white text-xl font-medium">
              Leave Type Name
            </h5>
          ) : (
            <Form.Item
              className="!mb-0  no-border"
              name={[name, 'name']}
              rules={[{ required: true, message: 'Please enter name' }]}
            >
              <Input
                className="!bg-transparent !border-none !p-0 text-white text-xl font-medium"
                placeholder="Enter leave type name"
              />
            </Form.Item>
          )}

          <button type="button" onClick={handleAction}>
            {status === 'created' ? (
              <PencilEdit02Icon
                size={20}
                className="text-theme-primary mt-0.5"
              />
            ) : (
              <CheckCircleIcon
                size={20}
                className="text-theme-primary mt-0.5"
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
};
export const TypeBody = ({
  name,
  form,
  isEditMode,
}: {
  name: number;
  form: FormInstance<any>;
  isEditMode: boolean;
}) => {
  const document = Form.useWatch(
    ['leave_types', name, 'is_document_needed'],
    form,
  );
  const leave_encashment = Form.useWatch(
    ['leave_types', name, 'is_leave_encashment_enable'],
    form,
  );
  const carry_forward = Form.useWatch(
    ['leave_types', name, 'is_carry_forward_enable'],
    form,
  );
  const { data: salaryStructureData } = useGetSalaryStructureQuery(null);

  useEffect(() => {
    if (!isEditMode) {
      if (leave_encashment) {
        form.setFieldsValue({
          leave_types: {
            [name]: {
              minimum_tenure_in_days: 180,
              leave_encashment_max_limit: 10,
              leave_encashment_eligible_for: ['full-time'],
              leave_encashment_rate_percentage: 0,
            },
          },
        });
      } else {
        form.resetFields([
          ['leave_types', name, 'minimum_tenure_in_days'],
          ['leave_types', name, 'leave_encashment_max_limit'],
          ['leave_types', name, 'leave_encashment_eligible_for'],
          ['leave_types', name, 'leave_encashment_rate_percentage'],
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leave_encashment, form]);

  useEffect(() => {
    if (!isEditMode) {
      if (carry_forward) {
        form.setFieldsValue({
          leave_types: {
            [name]: {
              carry_forward_max_limit: 10,
            },
          },
        });
      } else {
        form.resetFields(['carry_forward_max_limit']);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carry_forward, form]);

  useEffect(() => {
    if (!document && !isEditMode) {
      form.resetFields(['max_limit_for_require_document']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, form]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <Form.Item
          label="Annual Limit"
          name={[name, 'annual_limit']}
          rules={[{ required: true, message: 'Please enter annual limit' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select days"
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            options={createNumberOptions(1, 365)}
          />
        </Form.Item>

        <Form.Item
          label="Max At A Time"
          name={[name, 'max_at_a_time']}
          rules={[{ required: true, message: 'Please enter max at a time' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select"
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            options={createNumberOptions(1, 365)}
          />
        </Form.Item>

        <Form.Item
          label="Allow Previous Day"
          name={[name, 'allow_previous_day']}
          rules={[{ required: true, message: 'Please select option' }]}
        >
          <Select
            placeholder="Select"
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Line Manager Approval"
          name={[name, 'line_manager_approval']}
          rules={[{ required: true, message: 'Please select option' }]}
        >
          <Select
            placeholder="Select"
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </Form.Item>
      </div>

      <Divider className="bg-theme-gray-700" />
      <Form.Item
        label="Document Required"
        layout="horizontal"
        name={[name, 'is_document_needed']}
        valuePropName="checked"
        className="horizontal-end"
      >
        <Switch className="custom-switch" />
      </Form.Item>
      {!!document && (
        <>
          <Divider className="bg-transparent border-t border-t-theme-gray-700 border-dashed" />
          <Form.Item
            layout="horizontal"
            label="Max Limit For Require Document"
            name={[name, 'max_limit_for_require_document']}
            className="horizontal-end"
            rules={[{ required: true, message: 'Please enter max limit' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select"
              className="max-w-[357px] justify-end"
              suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
              options={createNumberOptions(1, 50, '')}
            />
          </Form.Item>
          <Divider className="bg-theme-gray-700" />
        </>
      )}

      <Divider className="bg-theme-gray-700" />
      <Form.Item
        label="Leave Encashment"
        layout="horizontal"
        name={[name, 'is_leave_encashment_enable']}
        valuePropName="checked"
        className="horizontal-end"
      >
        <Switch className="custom-switch" />
      </Form.Item>

      {!!leave_encashment && (
        <div>
          <Divider className="bg-transparent border-t border-t-theme-gray-700 border-dashed" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <Form.Item
              label="Minimum Tenure (Days)"
              name={[name, 'minimum_tenure_in_days']}
              rules={[
                { required: true, message: 'Please select minimum tenure' },
              ]}
            >
              <Select
                placeholder="Select days"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={createNumberOptions(1, 365)}
              />
            </Form.Item>

            <Form.Item
              label="Max Encashment Limit"
              name={[name, 'leave_encashment_max_limit']}
              rules={[{ required: true, message: 'Please enter max limit' }]}
            >
              <Select
                placeholder="Select limit"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={createNumberOptions(1, 365)}
              />
            </Form.Item>

            <Form.Item
              label="Eligible for"
              name={[name, 'leave_encashment_eligible_for']}
              rules={[
                { required: true, message: 'Please select eligible for' },
              ]}
            >
              <Select
                mode="multiple"
                options={EMPLOYEE_TYPES}
                placeholder="Select option"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
              />
            </Form.Item>
            <Form.Item
              label={
                <Flex align="center" gap={6}>
                  <span>Encashment Rate (%)</span>
                  <Tooltip
                    title={
                      <div className="bg-theme-slate-800 -mx-2 border border-theme-slate-700 -my-1.5 p-2 rounded-md text-left !w-[300px]">
                        <ul className="list-disc list-inside space-y-1">
                          <li>If user basic salary = 8000</li>
                          <li>Available leave to encashment days = 10 days</li>
                        </ul>

                        <Divider className="border-theme-gray-700 my-2" />

                        <ul className="list-disc list-inside space-y-1">
                          <li>Leave encashment rate = 50% of Basic Salary</li>
                          <li>
                            Total amount = (Basic Salary / 30) × (Rate / 100) ×
                            Encashment Days
                          </li>
                        </ul>

                        <Divider className="border-theme-gray-700 my-2" />

                        <div className="space-y-1">
                          <p className="font-medium">Calculation:</p>
                          <p>= 8000 / 30 × 50 / 100 × 10</p>
                          <p>= 266.67 × 0.5 × 10</p>
                          <p>= 133.33 × 10</p>
                          <p>= 1333.30</p>
                        </div>
                      </div>
                    }
                  >
                    <span className="text-theme-primary">
                      <InfoIcon size={16} />
                    </span>
                  </Tooltip>
                </Flex>
              }
              rules={[
                { required: true, message: 'Please enter encashment rate' },
              ]}
            >
              <Space.Compact className="w-full  items-center border border-theme-slate-700 rounded-md pr-1 bg-theme-slate-800">
                <Form.Item
                  name={[name, 'leave_encashment_rate_percentage']}
                  noStyle
                  rules={[{ required: true, message: 'Please select rate' }]}
                >
                  <Select
                    placeholder="Select rate"
                    className="!border-none"
                    suffixIcon={
                      <ChevronDownIcon size={24} className="text-white" />
                    }
                    options={[
                      { value: 0, label: '0%' },
                      { value: 10, label: '10%' },
                      { value: 20, label: '20%' },
                      { value: 50, label: '50%' },
                      { value: 100, label: '100%' },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  noStyle
                  name={[name, 'leave_encashment_rate_percentage_of']}
                  rules={[
                    { required: true, message: 'Please select component type' },
                  ]}
                >
                  <Select
                    size="small"
                    placeholder="Select"
                    className="!w-[160px] !border-none  compact-select"
                    placement="bottomRight"
                    onChange={() => {
                      form.setFields([
                        {
                          name: 'overtime_rate_per_hour_percent_of',
                          errors: [],
                        },
                      ]);
                    }}
                    options={[
                      ...(salaryStructureData?.data?.salary_structures?.map(
                        (structure: any) => ({
                          value: structure.component_name,
                          label: snakeToCamel(structure.component_name),
                        }),
                      ) || []),
                      { value: 'gross', label: 'Gross Salary' },
                    ]}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </div>
          <Divider className="bg-theme-gray-700" />
        </div>
      )}

      <Divider className="bg-theme-gray-700" />
      <Form.Item
        label="Carry Forward"
        name={[name, 'is_carry_forward_enable']}
        valuePropName="checked"
        layout="horizontal"
        className="horizontal-end"
      >
        <Switch className="custom-switch" />
      </Form.Item>

      {!!carry_forward && (
        <>
          <Divider className="bg-transparent border-t border-t-theme-gray-700 border-dashed" />
          <Form.Item
            label="Carry Forward Max Limit"
            layout="horizontal"
            name={[name, 'carry_forward_max_limit']}
            className="horizontal-end"
            rules={[{ required: true, message: 'Please enter max limit' }]}
          >
            <Select
              placeholder="Select limit"
              className="max-w-[357px] grow"
              suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
              options={createNumberOptions(1, 365)}
            />
          </Form.Item>
        </>
      )}
      <Divider className="bg-theme-gray-700" />
    </div>
  );
};

export default LeaveTypes;
